import { AlertTriangleIcon } from '@langchain/untitled-ui-icons';
import Divider from '@mui/joy/Divider';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';

import { useDeleteOAuthProviderMutation } from '@/Pages/Settings/hooks/useOAuthProviders';
import { Button } from '@/design-system/components/Button/Button';
import { Text } from '@/design-system/components/Text/Text';

interface DeleteOAuthProviderModalProps {
  isOpen: boolean;
  doClose: () => void;
  providerId: string;
  providerName: string;
  onSuccess?: () => void;
}

export function DeleteOAuthProviderModal({
  providerId,
  providerName,
  isOpen,
  doClose,
  onSuccess,
}: DeleteOAuthProviderModalProps) {
  const deleteProvider = useDeleteOAuthProviderMutation(providerId);

  const handleDelete = async () => {
    try {
      await deleteProvider.trigger({});
      onSuccess?.();
      doClose();
    } catch (error) {
      console.error('Failed to delete OAuth provider:', error);
    }
  };

  return (
    <Modal open={isOpen} onClose={() => doClose()}>
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        aria-labelledby="alert-dialog-modal-title"
        aria-describedby="alert-dialog-modal-description"
      >
        <div className="mb-2 flex items-center gap-2">
          <AlertTriangleIcon className="size-4" />
          <Text variant="h3" as="h2" id="alert-dialog-modal-title">
            Delete OAuth Provider
          </Text>
        </div>
        <Divider className="my-2" />
        <Text
          id="alert-dialog-modal-description"
          color="tertiary"
          className="mt-2"
        >
          Are you sure you want to delete the OAuth provider "{providerName}"?
          This action cannot be undone.
        </Text>
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              doClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="normal"
            color="error"
            loading={deleteProvider.isMutating}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </ModalDialog>
    </Modal>
  );
}
